import { useSyncExternalStore } from 'react'

import {
  DARK_QUERY,
  applyTheme,
  readChoice,
  resolveTheme,
  writeChoice,
  type ResolvedTheme,
  type ThemeChoice,
} from './theme'
import { applyScale, readScale, writeScale, type ScaleChoice } from './scale'

// ============================================================================
// THE THEME STORE — module level, deliberately, not a hook's useState.
//
// There are two consumers: the app shell, which mounts it for its side effects
// (keep <html> stamped, keep `<meta name="theme-color">` honest, follow the OS
// while a 'system' choice is in force), and the Appearance screen's control,
// which changes it. Two useState copies of the same fact drift the moment one of
// them re-stamps the DOM from a value the other has already replaced — and the
// symptom is a theme that silently reverts at sunset. One store, one truth.
//
// The <html> stamp already happened before React booted (the inline script in
// index.html), so nothing here causes a flash on load.
//
// SCALE RIDES ALONG in the same store (owner request, 2026-08-26 — see scale.ts
// for why), rather than a second module: it is stamped on the same element by
// the same subscribe/publish cycle, and the Appearance screen's Size control
// reads and writes it exactly the way the Theme control does.
// ============================================================================

export interface ThemeState {
  /** What the owner picked: 'light' | 'dark' | 'system'. */
  choice: ThemeChoice
  /** What that means right now. */
  resolved: ResolvedTheme
  /** What the machine is currently asking for, whatever the choice is. */
  systemIsDark: boolean
  setChoice: (next: ThemeChoice) => void
  /** What the owner picked: 'handoff' | 'comfortable' | 'large'. Default 'large' (130%). */
  scale: ScaleChoice
  setScale: (next: ScaleChoice) => void
}

function hasMediaQueries(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
}

function systemPrefersDark(): boolean {
  return hasMediaQueries() ? window.matchMedia(DARK_QUERY).matches : false
}

let choice: ThemeChoice = readChoice(typeof window === 'undefined' ? null : window.localStorage)
let systemIsDark = systemPrefersDark()
let scale: ScaleChoice = readScale(typeof window === 'undefined' ? null : window.localStorage)

/**
 * The snapshot is cached and only replaced when something actually changed:
 * `useSyncExternalStore` compares by identity and will loop forever on a fresh
 * object every read.
 */
let snapshot: Omit<ThemeState, 'setChoice' | 'setScale'> = {
  choice,
  resolved: resolveTheme(choice, systemIsDark),
  systemIsDark,
  scale,
}

const listeners = new Set<() => void>()

function publish() {
  const resolved = resolveTheme(choice, systemIsDark)
  if (
    snapshot.choice === choice &&
    snapshot.systemIsDark === systemIsDark &&
    snapshot.resolved === resolved &&
    snapshot.scale === scale
  ) {
    return
  }
  snapshot = { choice, resolved, systemIsDark, scale }
  if (typeof document !== 'undefined') {
    applyTheme(document, choice, resolved)
    applyScale(document, scale)
  }
  for (const listener of listeners) listener()
}

/**
 * THE OS CAN CHANGE WHILE THE APP IS OPEN — Windows flips at sunset if you let
 * it. Under a 'system' choice that has to move the app; under an explicit choice
 * it must not, which is why the listener records the FACT and `resolveTheme`
 * decides what to do with it, rather than the listener acting.
 */
let query: MediaQueryList | null = null
function watchSystem() {
  if (query || !hasMediaQueries()) return
  query = window.matchMedia(DARK_QUERY)
  query.addEventListener('change', (e) => {
    systemIsDark = e.matches
    publish()
  })
  systemIsDark = query.matches
}

function subscribe(listener: () => void): () => void {
  watchSystem()
  listeners.add(listener)
  // The store is created before the stylesheet is in the document, so the first
  // subscriber is the earliest honest moment to write the theme colour and the scale.
  if (typeof document !== 'undefined') {
    applyTheme(document, choice, snapshot.resolved)
    applyScale(document, scale)
  }
  publish()
  return () => void listeners.delete(listener)
}

function getSnapshot() {
  return snapshot
}

export function setThemeChoice(next: ThemeChoice): void {
  if (next === choice) return
  writeChoice(typeof window === 'undefined' ? null : window.localStorage, next)
  choice = next
  publish()
}

export function setScaleChoice(next: ScaleChoice): void {
  if (next === scale) return
  writeScale(typeof window === 'undefined' ? null : window.localStorage, next)
  scale = next
  publish()
}

/**
 * Mount anywhere; every caller reads the same store. The app shell mounts it for
 * the side effects alone — that is what keeps a 'system' choice following the
 * machine (and the scale applied) on screens that have no control for either
 * one on them.
 */
export function useTheme(): ThemeState {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return { ...state, setChoice: setThemeChoice, setScale: setScaleChoice }
}
